// src/v2/locations/LocationDrawerV2.jsx
//
// DRAWER-OFFICE-V3: Office peek drawer v3 rework.
//
// Props:
//   loc           object    — location row from the list payload
//   onClose       function  — called when the in-photo-header X is clicked
//   onOpenDetail  function  — called with loc.id to navigate to the full page
//
// Data fetched lazily on mount:
//   - listLocationLeases(loc.id)             → primary lease (expiry, cost)
//   - getProjects()                          → active projects for this location
//   - getPlan()                              → market plan intent (Related section)
//
// Section layout:
//   Photo          148 px
//   Header         100 px min
//   Space Fit       52 px min
//   Space & Occ    180 px min
//   Lease           72 px min
//   Key dates       92 px min
//   Related        (conditional)
//   Footer          58 px

import React, { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import { DrawerCloseButton } from "../components/DrawerCloseButton.jsx";
import { ReportingPeriodLabel } from "../../components/ReportingPeriodLabel.jsx";
import { T, STAGES } from "../../app/tokens.js";
import { StatusPill } from "../components/StatusPill.jsx";
import { buildLeaseUrl } from "../../services/buildUrl.js";
import { listLocationLeases } from "../../data/leasesClient.js";
import { getActiveLeaseTypes } from "../../utils/leases.js";
import { LEASE_DISPLAY } from "../../utils/leaseTypeConstants.js";
import { getCurrencySymbol } from "../../utils/format.js";
import { log } from "../../utils/logger.js";
import { getMAndATagName } from "../../utils/mAndA.js";
import { getDisplayedSqft, getAnalyticalSqft, isServicedNoArea } from "../../utils/locationSqftDerived.js";
import { listAnnualArea } from "../../shared/leaseAnnualHeadlines.js";
import {
  formatReportingPeriodStatus,
  selectReportingMonthRow,
} from "../../services/reportingPeriod.js";
import { ServicedNaLabel } from "../../components/ServicedNaLabel.jsx";
import { computeSpaceFit, SPACE_FIT_BG, SPACE_FIT_COLOR } from "../../utils/spaceFitVerdict.js";
import { computeOfficeSpaceFit } from "../../services/officeSeatFitPresentation.js";
import { HowWeDefineThis } from "../../components/SpacePolicyDefinitions.jsx";
import { useSpacePolicy } from "../../services/spacePolicyStore.js";
import { LEASE_STATUS_META } from "../../features/leases/leaseStatusLabels.js";
import { getPlan } from "../../data/marketsClient.js";
import { getProjects } from "../../data/projectsClient.js";
import { getLocation } from "../../data/locationsClient.js";
import { useFxRates } from "../../data/useFxRates.js";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { governedNavEnabled } from "../../app/governedNav.js";
import { formatLongExpiryDate, isActionablePastDueExpiry, isPastExpiryDate } from "../../features/leases/expiryDisplay.js";

// Drawer pill labels: both sublease directions collapse to "Sublease" per S1 spec.
const DRAWER_LEASE_DISPLAY = {
  ...LEASE_DISPLAY,
  sublease_inbound:  "Sublease",
  sublease_outbound: "Sublease",
};

// One-line explanation for each Space Fit verdict.
export const SPACE_FIT_EXPLANATION = {
  "Right-Sized":   "Assigned headcount fits installed seats with healthy room",
  "Over-Capacity": "People exceed available desks",
  "Over-Cap": "People exceed installed seats",
  "Tight": "Assigned headcount is at or near the installed-seat limit",
  "Dense":         "Build is physically cramped per sf/seat",
  "Under-Used":    "Desks are undersubscribed",
};

// ── Expiry RAG ────────────────────────────────────────────────────────────────
// Explicit year-band thresholds per spec — independent of lease type/optionality.
//   ≥7 yr  → Secure   (green)
//   3–7 yr → Moderate (amber)
//   <3 yr  → High     (red)
//   expired (past or no date) → High
/**
 * Returns { band, color, word } or null when endDateStr is absent/unparseable.
 */
function computeExpiryRag(endDateStr) {
  if (!endDateStr) return null;
  const d = new Date(String(endDateStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const yrsOut = (d.getTime() - Date.now()) / (365.25 * 86400000);
  if (yrsOut >= 7) return { band: "low",      color: T.greenDeep, word: "Secure"   };
  if (yrsOut >= 3) return { band: "moderate", color: T.ragAmber,  word: "Moderate" };
  return                  { band: "high",     color: T.ragRed,    word: "High"     };
}

// ── Pure helpers (exported for tests) ─────────────────────────────────────────

/**
 * Seat-assignment percentage (0–100) or null when inputs are missing/invalid.
 */
export function computeAssignedPct(loc) {
  const seats = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  const hc    = loc.latestHeadcount?.totalCount != null
    ? Number(loc.latestHeadcount.totalCount) : null;
  if (seats == null || hc == null || seats <= 0) return null;
  const pct = (hc / seats) * 100;
  return isFinite(pct) ? pct : null;
}

/**
 * Average utilisation percentage (0–100) or null.
 * loc.avgUtilPct comes from the API as a 0–1 decimal.
 */
export function computeUtilPct(loc) {
  if (loc.avgUtilPct == null) return null;
  const pct = Number(loc.avgUtilPct) * 100;
  return isFinite(pct) ? pct : null;
}

/**
 * Peak utilisation percentage (0–100) or null from peakUtil3moPct.
 */
export function computePeakPct(loc) {
  if (loc.peakUtil3moPct == null) return null;
  const pct = Number(loc.peakUtil3moPct) * 100;
  return isFinite(pct) ? pct : null;
}

/**
 * Seat-density percentage (0–100) or null.
 * installedCapacity / maxCapacity × 100.
 */
export function computeDensityPct(loc) {
  const installed = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  const maxCap    = loc.maxCapacity != null ? Number(loc.maxCapacity) : null;
  if (installed == null || maxCap == null || maxCap <= 0) return null;
  const pct = (installed / maxCap) * 100;
  return isFinite(pct) ? pct : null;
}

/**
 * Current-month label, e.g. "Jun 2026".
 * Kept for backward compatibility; prefer recentUtilMonth from the list API.
 */
export function currentMonthLabel(now = new Date()) {
  return now.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/**
 * Format a most-recent-complete-month ISO date string (YYYY-MM-DD) as
 * "Mon YYYY" (e.g. "Jun 2026"). Returns "—" when absent or unparseable.
 */
function fmtRecentUtilMonth(monthStr) {
  if (!monthStr) return "—";
  const d = new Date(String(monthStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/**
 * Whole-number of years from today to dateStr using month-floor arithmetic,
 * matching the Detail Overview's countdownPill logic.
 * Returns null when dateStr is absent or unparseable.
 * Returns a negative value when dateStr is in the past.
 */
export function yearsUntilFloor(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMonths = (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth());
  return Math.floor(diffMonths / 12);
}

/**
 * Compact relative-age pill for a future/past date.
 * e.g. "3d", "4 mo", "9 yrs". Returns null when the date is unparseable.
 */
export function relativeAgePill(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMs = Math.abs(now.getTime() - d.getTime());
  const days = Math.round(diffMs / 86400000);
  if (days < 30) return `${days}d`;
  const months = Math.round(days / 30.44);
  if (months < 24) return `${months} mo`;
  const years = Math.round(days / 365.25);
  return `${years} yr${years !== 1 ? "s" : ""}`;
}

/**
 * Format a lease expiry date as "MMM YYYY" (e.g. "May 2037").
 */
export function fmtExpiryLabel(endDate) {
  if (!endDate) return "—";
  const d = new Date(String(endDate).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/**
 * Whole-number of years from today to endDate.
 * Returns null when unparseable or in the past.
 */
export function computeYrsOut(endDate) {
  if (!endDate) return null;
  const d = new Date(String(endDate).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const yrs = Math.round((d.getTime() - Date.now()) / (365.25 * 86400000));
  return yrs;
}

/**
 * Format a peak-month ISO date string as "Mon 'YY" (e.g. "May '26").
 */
export function fmtPeakMonth(monthStr) {
  if (!monthStr) return null;
  const d = new Date(String(monthStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const mo = d.toLocaleDateString("en-US", { month: "short" });
  const yr = String(d.getFullYear()).slice(2);
  return `${mo} '${yr}`;
}

/**
 * Compact lease-cost label with currency symbol: "$10.2M/yr", "$480K/yr".
 */
export function fmtLeaseCost(val, currency = "USD") {
  if (val == null) return "—";
  const num = parseFloat(String(val).replace(/,/g, ""));
  if (isNaN(num)) return "—";
  const sym = getCurrencySymbol(currency) || "$";
  if (num >= 1_000_000) { const s = (num / 1_000_000).toFixed(2); return `${sym}${s.endsWith(".00") ? s.slice(0, -3) : s.endsWith("0") ? s.slice(0, -1) : s}M/yr`; }
  if (num >= 1_000)     return `${sym}${+(num / 1_000).toFixed(0)}K/yr`;
  return `${sym}${Math.round(num)}/yr`;
}

/**
 * Cost-per-sqft label: "$81.89 / sf". Returns null when either input is absent.
 */
export function fmtCostPerSf(cost, sqft) {
  if (cost == null || sqft == null || Number(sqft) <= 0) return null;
  const perSf = Number(cost) / Number(sqft);
  if (!isFinite(perSf)) return null;
  return `$${perSf.toFixed(2)} / sf`;
}

// ── Private helpers ────────────────────────────────────────────────────────────

/** Derive display bucket key from a lease object + location stage. */
function deriveLeaseBucket(lease, locStage) {
  if (!lease) return null;
  if (lease.type === "sublease_outbound") return "sublease_out";
  if (lease.status === "draft") return "draft";
  if (lease.status === "expired") return "expired";
  if (lease.status === "active" && locStage === "vacated") return "vacant_held";
  if (lease.status === "active" || lease.status === "holdover" || lease.status === "in_negotiation") return "in_use";
  return null;
}

/** Compute the renewal notice date by subtracting months from endDate. Returns a Date or null. */
function computeRenewalNoticeDate(endDateStr, renewalNoticeMonths) {
  if (!endDateStr || renewalNoticeMonths == null) return null;
  const d = new Date(String(endDateStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const months = Number(renewalNoticeMonths);
  if (!isFinite(months) || months <= 0) return null;
  const nd = new Date(d);
  nd.setMonth(nd.getMonth() - months);
  return nd;
}

/** Format a Date as "MMM YYYY". */
function fmtDateLabel(d) {
  if (!d || isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/** Compact sqft for KPI tile value: "125k sf", "1.2M sf", "750 sf", or "—". */
function fmtSqftCompact(sqft) {
  if (sqft == null) return "—";
  const n = Number(sqft);
  if (!isFinite(n)) return "—";
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M sf`;
  if (n >= 1_000)     return `${Math.round(n / 1_000)}k sf`;
  return `${Math.round(n)} sf`;
}

/** Cost per seat: "$26.5k", "$2.1M", etc. Returns "—" when inputs are missing. */
function fmtCostPerSeat(cost, seats, currency = "USD") {
  if (cost == null || seats == null || Number(seats) <= 0) return "—";
  const perSeat = Number(cost) / Number(seats);
  if (!isFinite(perSeat)) return "—";
  const sym = getCurrencySymbol(currency) || "$";
  if (perSeat >= 1_000_000) return `${sym}${+(perSeat / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (perSeat >= 1_000)     return `${sym}${+(perSeat / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${sym}${Math.round(perSeat)}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Single horizontal metric bar row (label · bar · pct). 8px track with gray base. */
function BarRow({ label, pct, displayPct, color, labelColor, testId, customValueNode }) {
  const clamped    = Math.max(0, Math.min(100, pct ?? 0));
  const shownPct   = displayPct != null ? displayPct : clamped;
  const shownColor = labelColor || "var(--v2-dark)";
  return (
    <div
      data-testid={testId}
      style={{ display: "flex", alignItems: "center", gap: 8, height: 20 }}
    >
      <span style={{
        fontSize: T.fsXs, color: T.hubSlate,
        width: 106, flexShrink: 0,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 8, background: "var(--v2-hair)", borderRadius: 4, position: "relative" }}>
        <div style={{
          position: "absolute", left: 0, top: 0,
          width: `${clamped}%`, height: "100%",
          background: color, borderRadius: 4,
          transition: "width .3s ease",
        }} />
      </div>
      <span style={{
        fontSize: T.fsXs, fontWeight: T.fwSemibold, color: shownColor,
        width: 30, textAlign: "right", flexShrink: 0,
      }}>
        {customValueNode != null ? customValueNode : `${Math.round(shownPct)}%`}
      </span>
    </div>
  );
}

/** KPI tile: label (uppercase caption) + value (large) + descriptor (muted one-liner). */
function KpiTile({ label, value, descriptor, testId, valueColor, title }) {
  return (
    <div
      data-testid={testId}
      title={title}
      style={{
        background: T.hoverBg, borderRadius: 7,
        padding: "7px 9px",
        display: "flex", flexDirection: "column", gap: 2,
      }}
    >
      <div style={{
        fontSize: T.fsXxxs5, fontWeight: T.fwBold, color: T.light,
        textTransform: "uppercase", letterSpacing: ".07em",
      }}>
        {label}
      </div>
      <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: valueColor ?? "var(--v2-dark)", lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: T.fsXxxs5, color: T.light }}>
        {descriptor}
      </div>
    </div>
  );
}

/** Two-column brow row: label left, value right. */
function BrowRow({ label, children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex", alignItems: "baseline",
        justifyContent: "space-between",
        gap: 8, minHeight: 24, padding: "3px 0",
      }}
    >
      <span style={{
        fontSize: T.fsXxs5, fontWeight: T.fwSemibold, color: T.light,
        flexShrink: 0, whiteSpace: "nowrap",
      }}>
        {label}
      </span>
      <span style={{
        fontSize: T.fsXxs5, color: "var(--v2-dark)",
        textAlign: "right", minWidth: 0,
      }}>
        {children}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function LocationDrawerV2(props) {
  return props.loc ? <LocationDrawerContent {...props} /> : null;
}

function LocationDrawerContent({ loc, onClose, onOpenDetail }) {
  useSpacePolicy();
  const { flags, flagsReady, nav_config, nav_order } = useFeatureFlagContext();
  const projectsNavEnabled = flagsReady && governedNavEnabled(flags, nav_config, "projects", nav_order);
  const planNavEnabled = flagsReady && governedNavEnabled(flags, nav_config, "plan", nav_order);
  const [primaryLease, setPrimaryLease] = useState(null);   // null=loading, false=none
  const [projects,     setProjects]     = useState(null);   // null=loading, []|array when done
  const [planData,     setPlanData]     = useState(null);   // null=loading, []|array when done
  const [detailLoc,    setDetailLoc]    = useState(null);   // null=loading, false=none/error, object=loaded
  useFxRates(); // keeps the FX hook subscribed for future use; rates no longer needed client-side

  // ── Lease + key-date fetch ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function fetchLeaseData() {
      try {
        const leases = await listLocationLeases(loc.id);
        if (cancelled) return;

        const primary = Array.isArray(leases)
          ? (leases.find((l) => l.status === "active" || l.status === "holdover") ?? leases[0] ?? null)
          : null;

        setPrimaryLease(primary || false);
      } catch (err) {
        log.warn("LocationDrawerV2: leases fetch failed", { id: loc.id, err: String(err) });
        if (!cancelled) {
          setPrimaryLease(false);
        }
      }
    }

    fetchLeaseData();
    return () => { cancelled = true; };
  }, [loc.id]);

  // ── Projects fetch ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectsNavEnabled) {
      setProjects(null);
      return undefined;
    }
    let cancelled = false;
    setProjects(null);
    getProjects()
      .then((list) => {
        if (!cancelled) setProjects(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        log.warn("LocationDrawerV2: projects fetch failed", { id: loc.id, err: String(err) });
        if (!cancelled) setProjects([]);
      });
    return () => { cancelled = true; };
  }, [loc.id, projectsNavEnabled]);

  // ── Plan fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!planNavEnabled) {
      setPlanData(null);
      return undefined;
    }
    let cancelled = false;
    setPlanData(null);
    getPlan().catch(() => null).then((plan) => {
      if (cancelled) return;
      setPlanData(Array.isArray(plan) ? plan : (plan ? [] : false));
    });
    return () => { cancelled = true; };
  }, [loc.id, planNavEnabled]);

  // ── Detail location fetch (for peak data parity with Overview tab) ────────
  // The detail response carries the canonical period and the latest rows up to
  // that month, allowing the drawer and Overview card to share selection rules.
  useEffect(() => {
    let cancelled = false;
    getLocation(loc.id)
      .then((detail) => {
        if (!cancelled) setDetailLoc(detail || false);
      })
      .catch((err) => {
        log.warn("LocationDrawerV2: detail fetch failed", { id: loc.id, err: String(err) });
        if (!cancelled) setDetailLoc(false);
      });
    return () => { cancelled = true; };
  }, [loc.id]);

  // ── Derived values ────────────────────────────────────────────────────────

  const leaseTypes = getActiveLeaseTypes(loc);
  const stageInfo  = STAGES[loc.stage];
  const mAndALabel = loc.mAndA ? getMAndATagName(loc.mAndA) : null;

  const installedSeats = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  // VACATED-DETAIL-RETENTION (O-27): prefer retained pre-exit seat count for
  // display purposes only.  installedSeats (above) keeps the effective
  // capacity-schedule value (0 for vacated) so all derived calculations
  // (headroom, over-capacity, SpaceFit) stay correct/blank for vacated offices.
  const displayedSeats = loc.displayInstalledCapacity != null
    ? Number(loc.displayInstalledCapacity)
    : installedSeats;
  const isSeatsRetained = (loc.stage === "vacated" || loc.stage === "retired") && loc.displayInstalledCapacity != null;
  const assignedHc     = loc.latestHeadcount?.totalCount != null
    ? Number(loc.latestHeadcount.totalCount) : null;
  const maxCap         = loc.maxCapacity != null ? Number(loc.maxCapacity) : null;
  const isOverCapacity = installedSeats != null && assignedHc != null && assignedHc > installedSeats;
  const overage        = isOverCapacity ? (assignedHc - installedSeats) : null;
  const openSeats      = installedSeats != null && assignedHc != null && !isOverCapacity
    ? Math.max(0, installedSeats - assignedHc) : null;
  const headroomSeats  = installedSeats != null && maxCap != null
    ? Math.max(0, maxCap - installedSeats) : null;
  const rawHeadroom    = installedSeats != null && maxCap != null
    ? maxCap - installedSeats : null;
  const headroomColor  = rawHeadroom == null ? undefined
    : rawHeadroom > 0 ? T.ragGreenText
    : rawHeadroom < 0 ? T.ragRed
    : T.mid;

  const assignedPct = computeAssignedPct(loc);
  const densityPct  = computeDensityPct(loc);

  // Use the same canonical month selection as the detail Overview. A resolved
  // detail response with no usable row stays empty instead of reverting to an
  // unrelated older list value.
  const reportingPeriod = detailLoc?.reportingPeriod ?? loc.reportingPeriod;
  const detailAvailable = detailLoc !== null && detailLoc !== false
    && typeof detailLoc === "object";
  const detailUtil = (() => {
    if (!detailAvailable) return null;
    const rows = Array.isArray(detailLoc.utilization) ? detailLoc.utilization : [];
    return selectReportingMonthRow(rows, reportingPeriod);
  })();
  const detailPeakBadges = detailUtil?.peakDailyUniqueBadges ?? null;
  const detailPeakMonth  = detailUtil?.month ?? null;
  const detailPeakSourceLabel = detailUtil?.sourceLabel ?? null;
  const peakAttCount = detailAvailable
    ? (detailPeakBadges != null ? Math.round(Number(detailPeakBadges)) : null)
    : (loc.recentPeakBadges != null ? Math.round(Number(loc.recentPeakBadges)) : null);
  const peakAttPct = detailAvailable
    ? (detailPeakBadges != null && installedSeats != null && installedSeats > 0
        ? (Number(detailPeakBadges) / installedSeats) * 100
        : null)
    : (loc.recentPeakPct != null ? Number(loc.recentPeakPct) * 100 : null);
  const peakSourceLabel = detailAvailable
    ? detailPeakSourceLabel
    : (loc.recentPeakSourceLabel ?? null);

  const derivedSf     = getDisplayedSqft(loc, null);
  // VACATED-DETAIL-RETENTION (O-27): prefer retained pre-exit sqft for display.
  // derivedSf (not displaySf) is passed to computeSpaceFit so the verdict stays
  // blank for vacated offices (derivedSf is 0/null after sqft_sync zeroes it).
  const displaySf = (loc.stage === "vacated" || loc.stage === "retired") && loc.displayInstalledSqft != null
    ? loc.displayInstalledSqft
    : derivedSf;
  const isSqftRetained = (loc.stage === "vacated" || loc.stage === "retired") && loc.displayInstalledSqft != null;

  // Space Fit signal.
  const { verdict: sfVerdict, dense: sfDense } = computeOfficeSpaceFit(loc);
  const sfBg    = sfVerdict ? SPACE_FIT_BG[sfVerdict]    : null;
  const sfColor = sfVerdict ? SPACE_FIT_COLOR[sfVerdict] : null;
  const sfExplanation = sfVerdict ? SPACE_FIT_EXPLANATION[sfVerdict] : null;

  // Lease section derived values.
  const lease          = primaryLease && primaryLease !== false ? primaryLease : null;
  const leaseEndDate   = lease?.endDate ?? null;
  // annualRentUsd: canonical current-year contractual rent (pre-abatement) in USD,
  // resolved by the list endpoint via resolveCurrentYearRentUsd. Never fall back to
  // lease.totalCost here — that stale field is not the annual rent.
  const leaseCostUsd       = lease?.contractualAnnualRentUsd !== undefined ? lease.contractualAnnualRentUsd : lease?.annualRentUsd ?? null;
  // annualAbatementUsd: row-level abatement for the same year-cost row, in USD.
  // Non-zero only when lease_year_costs.abatement is set for that row.
  const leaseAbatementUsd  = (lease?.annualAbatementUsd ?? 0) > 0 ? lease.annualAbatementUsd : null;
  // Net rent: contractual minus abatement. Only computed when abatement > 0.
  const leaseCostNetUsd    = (leaseCostUsd != null && leaseAbatementUsd != null)
    ? leaseCostUsd - leaseAbatementUsd : null;
  const leaseSize = lease ? listAnnualArea(lease) : null;
  const expiryLabel    = fmtExpiryLabel(leaseEndDate);
  const pastDueActionable = isActionablePastDueExpiry(lease, {
    date: leaseEndDate,
    officeStage: loc.stage,
  });
  const expiryDisplayLabel = isPastExpiryDate(leaseEndDate)
    ? formatLongExpiryDate(leaseEndDate)
    : expiryLabel;
  // Use month-floor arithmetic (matching the Detail Overview's countdownPill)
  // so drawer and detail always agree on the displayed year count.
  const yrsOut         = yearsUntilFloor(leaseEndDate);
  const costLabel      = fmtLeaseCost(leaseCostUsd, "USD");
  const costNetLabel   = leaseCostNetUsd != null ? fmtLeaseCost(leaseCostNetUsd, "USD") : null;
  const costPerSf      = fmtCostPerSf(leaseCostUsd, leaseSize);

  // Expiry RAG: purely year-band based per spec.
  const expiryRag      = computeExpiryRag(leaseEndDate);
  const expiryColor    = expiryRag
    ? (isPastExpiryDate(leaseEndDate) && !pastDueActionable ? T.mid : expiryRag.color)
    : T.mid;
  const expiryBandWord = expiryRag ? expiryRag.word  : null;

  // Lease status bucket (for chip row).
  // Fallback chain: derived from primary lease → loc.leaseStatus (pre-computed) → null.
  const leaseStatusBucket = deriveLeaseBucket(lease, loc.stage)
    ?? (loc.leaseStatus ? (LEASE_STATUS_META[loc.leaseStatus] ? loc.leaseStatus : null) : null);
  const leaseStatusMeta   = leaseStatusBucket ? (LEASE_STATUS_META[leaseStatusBucket] ?? null) : null;

  // Key dates: Renewal Notice.
  const renewalNoticeDate = lease
    ? computeRenewalNoticeDate(leaseEndDate, lease.renewalNoticeMonths)
    : null;
  const renewalNoticeDateLabel = fmtDateLabel(renewalNoticeDate);
  // Use month-floor arithmetic here too for consistency with Detail Overview.
  const renewalNoticeYrsOut    = renewalNoticeDate
    ? yearsUntilFloor(renewalNoticeDate.toISOString().slice(0, 10))
    : null;

  // Active projects for this location.
  // Support both `status` (spec field) and `phase` (legacy field) to be robust.
  const activeProjects = Array.isArray(projects)
    ? projects.filter((p) => {
        if (p.locationId !== loc.id) return false;
        const state = p.status ?? p.phase;
        return state !== "complete" && state !== "cancelled";
      })
    : null;
  const projectsLabel = activeProjects === null
    ? null
    : activeProjects.length === 0
      ? "—"
      : `${activeProjects.map((p) => p.name || p.title || "Project").join(", ")} · ${activeProjects.length} active`;

  // Related section: plan market.
  const planMarket = planNavEnabled && Array.isArray(planData)
    ? planData.find((m) =>
        (loc.marketId && m.marketId === loc.marketId) ||
        (loc.market && m.market === loc.market)
      )
    : null;

  // Both the header and Peak day descriptor expose canonical month, data lag,
  // and aggregate partial coverage from the same resolver metadata.
  const peakMonth = detailAvailable ? detailPeakMonth : (loc.recentUtilMonth ?? null);
  const periodStatus = formatReportingPeriodStatus(
    reportingPeriod,
    peakMonth,
    { emptyShowsTarget: true },
  );
  const sectionPeriodStatus = periodStatus?.replace(/^As of /, "as of ");

  const hasImg = !!loc.imageUrl;

  // ── Badge integration flag ────────────────────────────────────────────────
  // OFFICES-PEAK-ATTENDANCE-PROVENANCE: while util_live is integration_pending,
  // all peak-attendance values come from the migration 0075 seed and must not
  // be presented as real measurements.  The server injects this flag on every
  // list row and detail response so the client never shows fabricated numbers.
  const badgeIntegrationPending = !!loc.badgeIntegrationPending;

  // O-30: vacated/retired/closed offices must never show a peak count — the
  // historical utilization data is stale/meaningless once the office exits.
  const isVacatedStage = loc.stage === "vacated" || loc.stage === "retired" || loc.stage === "closed";

  // ── KPI tile values ───────────────────────────────────────────────────────

  // Row 1
  const kpiPeopleVal  = assignedHc != null ? assignedHc.toLocaleString() : "—";
  // Peak day: suppress only when office is vacated/retired/closed.
  // O-34: badgeIntegrationPending no longer suppresses offices that have real
  // peak data; offices with no data will naturally render "—" because peakAttCount is null.
  const kpiPeakVal    = isVacatedStage
    ? "—"
    : peakAttCount != null
      ? `${peakAttCount}${peakAttPct != null ? ` · ${Math.round(peakAttPct)}%` : ""}`
      : "—";
  const kpiSqftVal    = displaySf != null
    ? fmtSqftCompact(displaySf)
    : isServicedNoArea(loc)
      ? <ServicedNaLabel />
      : "—";

  // Row 2
  const kpiSeatsVal   = displayedSeats != null ? displayedSeats.toLocaleString() : "—";
  const kpiHeadroomVal = rawHeadroom != null
    ? rawHeadroom > 0 ? `+${rawHeadroom.toLocaleString()}`
    : rawHeadroom < 0 ? `\u2212${Math.abs(rawHeadroom).toLocaleString()}`
    : "0"
    : "—";
  const kpiCostSeatVal = fmtCostPerSeat(leaseCostUsd, installedSeats, "USD");

  return (
    <div
      data-testid="v2-drawer-v2"
      style={{ height: "100%", display: "flex", flexDirection: "column", background: "var(--v2-card)" }}
    >

      {/* ── Photo: 148 px ───────────────────────────────────────────────────── */}
      <div style={{ position: "relative", height: 148, flexShrink: 0, overflow: "hidden", background: "var(--v2-tint)" }}>
        {hasImg ? (
          <img
            src={loc.imageUrl}
            alt={loc.market}
            data-testid="v2-drawer-photo"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            data-testid="v2-drawer-photo-placeholder"
            style={{
              width: "100%", height: "100%",
              background: T.drawerPhotoGradient,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Building2 size={40} color={T.whiteFade} />
          </div>
        )}
        <DrawerCloseButton onClick={onClose} testId="v2-drawer-close" />
      </div>

      {/* ── Scrollable body ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto" }}>

        {/* ── Header: min-height 100px ───────────────────────────────────────── */}
        <div
          data-testid="v2-drawer-header"
          style={{ padding: "13px 18px 12px", borderBottom: "1px solid var(--v2-hair)", minHeight: 100 }}
        >
          {/* Eyebrow: location ID */}
          <div style={{ fontSize: T.fsXxs5, color: T.light, fontWeight: T.fwSemibold, marginBottom: 4 }}>
            {loc.id}
          </div>

          {/* Title row: Name · Alias · Hub glyph (inline) · M&A */}
          <div
            role="button"
            tabIndex={0}
            data-testid={`v2-drawer-title-${loc.id}`}
            aria-label={`Open ${loc.market}${loc.locationAlias ? " · " + loc.locationAlias : ""}`}
            onClick={() => onOpenDetail(loc.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onOpenDetail(loc.id);
              }
            }}
            style={{
              display: "flex", alignItems: "baseline", marginBottom: 4,
              whiteSpace: "nowrap", overflow: "hidden",
              cursor: "pointer", outline: "none",
            }}
          >
            <span style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, color: "var(--v2-dark)", whiteSpace: "nowrap" }}>
              {loc.market}
            </span>
            {loc.locationAlias && (
              <>
                <span style={{ fontSize: T.fsBase, color: T.borderDark, margin: "0 5px", flexShrink: 0 }}>·</span>
                <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: T.light, whiteSpace: "nowrap" }}>
                  {loc.locationAlias}
                </span>
              </>
            )}
            {loc.isHub && (
              <>
                <span style={{ fontSize: T.fsBase, color: T.borderDark, margin: "0 5px", flexShrink: 0 }}>·</span>
                <span
                  data-testid="v2-drawer-hub-glyph"
                  style={{
                    fontSize: T.fsXxs5, fontWeight: T.fwSemibold,
                    color: T.hubChipText, whiteSpace: "nowrap",
                  }}
                >
                  ◆ Hub
                </span>
              </>
            )}
            {mAndALabel && (
              <>
                <span style={{ fontSize: T.fsBase, color: T.borderDark, margin: "0 5px", flexShrink: 0 }}>·</span>
                <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: T.light, whiteSpace: "nowrap" }}>
                  {mAndALabel}
                </span>
              </>
            )}
          </div>

          {/* Address */}
          <div
            data-testid="v2-drawer-address"
            style={{
              fontSize: T.fsXs, color: T.mid,
              marginBottom: 6,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {loc.address || ""}
          </div>

          {/* Hairline divider between address and chips */}
          <div
            data-testid="v2-drawer-address-divider"
            style={{ borderBottom: "1px solid var(--v2-hair)", marginBottom: 8 }}
          />

          {/* Chip row: three labeled groups always rendered — Office stage · Lease status · Lease type */}
          <div
            data-testid="v2-drawer-pills"
            style={{ display: "flex", gap: 16, flexWrap: "nowrap", alignItems: "flex-start" }}
          >
            <StatusPill
              label="Office stage:"
              value={stageInfo ? stageInfo.label : "—"}
              bg={stageInfo ? stageInfo.bg : undefined}
              fg={stageInfo ? stageInfo.color : undefined}
              testId="v2-drawer-chip-stage"
            />
            <StatusPill
              label="Lease status:"
              value={leaseStatusMeta ? leaseStatusMeta.label : "—"}
              bg={leaseStatusMeta ? leaseStatusMeta.bg : undefined}
              fg={leaseStatusMeta ? leaseStatusMeta.color : undefined}
              testId="v2-drawer-chip-lease-status"
            />
            <StatusPill
              label="Lease type:"
              value={leaseTypes.length > 0 ? (DRAWER_LEASE_DISPLAY[leaseTypes[0]] ?? leaseTypes[0]) : "—"}
              testId="v2-drawer-chip-lease-type"
            />
          </div>
        </div>

        {/* ── Space Fit signal: min-height 76px ──────────────────────────────── */}
        <div
          data-testid="v2-drawer-space-fit-section"
          style={{
            padding: "10px 18px",
            borderBottom: "1px solid var(--v2-hair)",
            minHeight: 76,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
          }}
        >
          {sfVerdict && (
            <div
              data-testid="v2-drawer-space-fit"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              {/* 44×44 Square RAG indicator */}
              <span
                data-testid="v2-drawer-space-fit-square"
                style={{
                  display: "inline-block",
                  width: 44, height: 44, borderRadius: 11, flexShrink: 0,
                  background: sfBg,
                }}
              />
              <div>
                <div style={{
                  fontSize: T.fsXs5, fontWeight: T.fwBold, color: sfColor, lineHeight: 1.2,
                }}>
                  {sfVerdict}
                </div>
                <div style={{ fontSize: T.fsXxxs5, color: T.light, lineHeight: 1.4 }}>
                  Space Fit
                </div>
                {sfExplanation && (
                  <div style={{ fontSize: T.fsXxs5, color: T.mid, lineHeight: 1.4, marginTop: 1 }}>
                    {sfExplanation}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Space & Occupancy: min-height 180px ───────────────────────────── */}
        <div
          data-testid="v2-drawer-space-section"
          style={{ padding: "12px 18px", borderBottom: "1px solid var(--v2-hair)", minHeight: 180 }}
        >
          {/* Section header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{
              fontSize: T.fsXxxs5, fontWeight: T.fwBold, color: T.light,
              letterSpacing: ".08em", textTransform: "uppercase",
            }}>
              {sectionPeriodStatus ? (
                <ReportingPeriodLabel
                  reportingPeriod={reportingPeriod}
                  infoTestId="drawer-coverage-info"
                >
                  {`Space & Occupancy · ${sectionPeriodStatus}`}
                </ReportingPeriodLabel>
              ) : "Space & Occupancy"}
            </span>
          </div>

          {/* KPI Row 1: Sq ft · Seats · People */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
            gap: 5, marginBottom: 5,
          }}>
            <KpiTile
              testId="v2-drawer-kpi-sqft"
              label="Sq ft"
              value={kpiSqftVal}
              descriptor="leased footprint"
              valueColor={isSqftRetained ? T.light : undefined}
            />
            <KpiTile
              testId="v2-drawer-kpi-seats"
              label="Seats"
              value={kpiSeatsVal}
              descriptor="installed desks"
              valueColor={isSeatsRetained ? T.light : undefined}
            />
            <KpiTile
              testId="v2-drawer-kpi-people"
              label="People"
              value={kpiPeopleVal}
              descriptor="on record · FT+CW+IC"
              title="Workday headcount on record for this office (source: location_headcount — used for utilization scoring)"
            />
          </div>

          {/* KPI Row 2: Peak day · Expansion · Lease/seat */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
            gap: 5, marginBottom: 11,
          }}>
            <KpiTile
              testId="v2-drawer-kpi-peak"
              label="Peak day"
              value={kpiPeakVal}
              descriptor={
                isVacatedStage
                  ? "—"
                  : peakAttCount != null
                    ? periodStatus ?? `As of ${fmtRecentUtilMonth(peakMonth)}`
                    + (peakSourceLabel ? ` · Source: ${peakSourceLabel}` : "")
                    : badgeIntegrationPending
                    ? "Awaiting badge feed"
                    : "—"
              }
            />
            <KpiTile
              testId="v2-drawer-kpi-headroom"
              label="Expansion capacity"
              value={kpiHeadroomVal}
              descriptor="room to add before max density"
              valueColor={headroomColor}
            />
            <KpiTile
              testId="v2-drawer-kpi-cost-seat"
              label="Lease / seat"
              value={kpiCostSeatVal}
              descriptor="lease rent ÷ installed seats"
            />
          </div>

          {/* VACATED-OFFICE-ASSIGNED-HC-FLAG (O-28): data-quality flag shown
              when the office is vacated/retired but Workday still has HC
              assigned. The operational "0 assigned" figure above is correct;
              this note surfaces the raw Workday count alongside it. */}
          {(loc.stage === "vacated" || loc.stage === "closed") && (loc.workdayHc ?? 0) > 0 && (
            <div
              data-testid="v2-drawer-workday-hc-flag"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                marginBottom: 8,
                padding: "5px 8px",
                borderRadius: 6,
                background: T.ragAmberBg ?? "rgba(245,158,11,0.10)",
                border: `1px solid ${T.ragAmber}`,
              }}
            >
              <span style={{ fontSize: T.fsXs, color: T.ragAmber, fontWeight: T.fwSemibold, flexShrink: 0 }}>⚠</span>
              <span style={{ fontSize: T.fsXxs5, color: T.ragAmber }}>
                {loc.workdayHc.toLocaleString()} still assigned in Workday — reassign
              </span>
            </div>
          )}

          {/* Seat assignment bar */}
          <BarRow
            testId="v2-drawer-bar-assignment"
            label="Seat assignment"
            pct={assignedPct}
            displayPct={isOverCapacity && assignedPct != null ? assignedPct : undefined}
            color={isOverCapacity ? T.ragRed : "var(--v2-accent)"}
            labelColor={isOverCapacity ? T.ragRed : undefined}
          />
          <div style={{
            fontSize: T.fsXxs, color: T.light,
            paddingLeft: 114, marginTop: 1, marginBottom: 7,
            height: 14, lineHeight: "14px",
          }}>
            {assignedHc != null && isOverCapacity
              ? `${assignedHc.toLocaleString()} assigned · ${overage.toLocaleString()} over capacity`
              : assignedHc != null && openSeats != null
                ? `${assignedHc.toLocaleString()} assigned · ${openSeats.toLocaleString()} open seats`
                : ""}
          </div>

          {/* Seat density bar */}
          <BarRow
            testId="v2-drawer-bar-density"
            label="Seat density"
            pct={densityPct}
            color={T.ragAmber}
            customValueNode={densityPct == null && isServicedNoArea(loc)
              ? <ServicedNaLabel compact />
              : undefined}
          />
          <div style={{
            fontSize: T.fsXxs, color: T.light,
            paddingLeft: 114, marginTop: 1,
            height: 14, lineHeight: "14px",
          }}>
            {installedSeats != null && headroomSeats != null
              ? `${installedSeats.toLocaleString()} installed · ${headroomSeats.toLocaleString()} expansion`
              : (densityPct == null && isServicedNoArea(loc) ? "serviced · area not tracked" : "")}
          </div>
        </div>

        {/* ── Lease: min-height 72px ────────────────────────────────────────── */}
        <div
          data-testid="v2-drawer-lease-section"
          style={{ padding: "12px 18px", borderBottom: "1px solid var(--v2-hair)", minHeight: 72 }}
        >
          <div style={{
            fontSize: T.fsXxxs5, fontWeight: T.fwBold, color: T.light,
            letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 6,
          }}>
            Lease
          </div>

          {/* Expiry brow row — RAG colored by year-band */}
          <BrowRow label="Expiry" testId="v2-drawer-expiry-row">
            {primaryLease === null ? (
              <span style={{ color: T.light }}>—</span>
            ) : (
              <span
                data-testid="v2-drawer-expiry-rag-value"
                style={{ color: expiryColor, fontWeight: T.fwSemibold }}
              >
                {expiryDisplayLabel}
                {yrsOut != null && ` · ${yrsOut} yr${Math.abs(yrsOut) !== 1 ? "s" : ""}`}
                {expiryBandWord && ` · ${expiryBandWord}`}
              </span>
            )}
          </BrowRow>

          {/* Rent brow row */}
          <BrowRow label="Annual rent" testId="v2-drawer-rent-row">
            {primaryLease === null ? (
              <span style={{ color: T.light }}>—</span>
            ) : (
              <span>
                {costLabel}
                {costPerSf
                  ? <span style={{ color: T.light }}>{" · "}{costPerSf}</span>
                  : isServicedNoArea(loc)
                    ? <span style={{ color: T.light }}>{" · "}<ServicedNaLabel /></span>
                    : null
                }
                {costNetLabel && (
                  <span style={{ color: T.light }}>{" · "}
                    <span data-testid="v2-drawer-rent-net">{costNetLabel}</span>
                    {" net"}
                  </span>
                )}
              </span>
            )}
          </BrowRow>
        </div>

        {/* ── Key dates & activity: min-height 92px ────────────────────────── */}
        <div
          data-testid="v2-drawer-key-dates-section"
          style={{ padding: "12px 18px", borderBottom: "1px solid var(--v2-hair)", minHeight: 92 }}
        >
          <div style={{
            fontSize: T.fsXxxs5, fontWeight: T.fwBold, color: T.light,
            letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 6,
          }}>
            Key dates &amp; activity
          </div>

          {/* Renewal Notice row */}
          <BrowRow label="Renewal notice" testId="v2-drawer-renewal-notice-row">
            {renewalNoticeDate ? (
              <span>
                {renewalNoticeDateLabel}
                {renewalNoticeYrsOut != null && ` · in ${renewalNoticeYrsOut} yr${Math.abs(renewalNoticeYrsOut) !== 1 ? "s" : ""}`}
              </span>
            ) : (
              <span style={{ color: T.light }}>—</span>
            )}
          </BrowRow>

          {/* Projects row */}
          {projectsNavEnabled && (
            <BrowRow label="Projects · improvements" testId="v2-drawer-projects-row">
              {projectsLabel !== null ? (
                <span style={{
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  display: "block", maxWidth: 180,
                }}>
                  {projectsLabel}
                </span>
              ) : (
                <span style={{ color: T.light }}>—</span>
              )}
            </BrowRow>
          )}
        </div>

        {/* ── Related section (conditional) ─────────────────────────────────── */}
        {(lease || planMarket) && (
          <div
            data-testid="v2-drawer-related-section"
            style={{ padding: "12px 18px", borderBottom: "1px solid var(--v2-hair)" }}
          >
            <div style={{
              fontSize: T.fsXxxs5, fontWeight: T.fwBold, color: T.light,
              letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 8,
            }}>
              Related
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* Lease card */}
              {lease && (
                <a
                  data-testid="v2-drawer-related-lease"
                  href={buildLeaseUrl(lease.id)}
                  style={{
                    display: "block",
                    background: T.hoverBg, borderRadius: 7,
                    padding: "8px 11px", cursor: "pointer",
                    textDecoration: "none", color: "inherit",
                  }}
                >
                  <div style={{ fontSize: T.fsXxs5, fontWeight: T.fwSemibold, color: "var(--v2-accent)" }}>
                    Lease →
                  </div>
                  <div style={{ fontSize: T.fsXxxs5, color: T.light, marginTop: 2 }}>
                    {lease.id}
                  </div>
                </a>
              )}

              {/* Market Plan card */}
              {planMarket && (
                <div
                  data-testid="v2-drawer-related-plan"
                  onClick={() => onOpenDetail(loc.id)}
                  style={{
                    background: T.hoverBg, borderRadius: 7,
                    padding: "8px 11px", cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: T.fsXxs5, fontWeight: T.fwSemibold, color: "var(--v2-dark)" }}>
                    Market Plan → {planMarket.market || loc.market}
                  </div>
                  <div style={{ fontSize: T.fsXxxs5, color: T.light, marginTop: 2 }}>
                    {[
                      planMarket.marketIntent || null,
                      planMarket.avgUtilPct != null
                        ? `${Math.round(Number(planMarket.avgUtilPct) * 100)}% avg util`
                        : null,
                      planMarket.totalHeadroom != null
                        ? planMarket.totalHeadroom > 0
                          ? `+${planMarket.totalHeadroom} expansion`
                          : planMarket.totalHeadroom < 0
                            ? `\u2212${Math.abs(planMarket.totalHeadroom)} expansion`
                            : `0 expansion`
                        : null,
                    ].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
              )}


            </div>
          </div>
        )}

      </div>

      {/* ── Footer: 58px ──────────────────────────────────────────────────────── */}
      <div style={{
        padding: "12px 18px", height: 58, flexShrink: 0,
        display: "flex", alignItems: "center",
        borderTop: "1px solid var(--v2-line)",
      }}>
        <button
          type="button"
          data-testid={`v2-drawer-open-${loc.id}`}
          onClick={() => onOpenDetail(loc.id)}
          style={{
            display: "block", width: "100%", textAlign: "center",
            background: "var(--v2-accent)", color: T.white,
            border: 0, borderRadius: 8, padding: "10px 14px",
            fontSize: T.fsBase, fontWeight: T.fwSemibold, cursor: "pointer",
            letterSpacing: ".01em",
          }}
        >
          Open location →
        </button>
      </div>
    </div>
  );
}
